<template>
	<div ref="zoneRef" class="future-referencing-zone">
		<div v-if="!isCardView" class="future-referencing-zone__header">
			<v-icon class="is-future">event_upcoming</v-icon>
			<span class="future-referencing-zone__title">{{ t('FutureReferencing') }}</span>
			<v-chip size="small" color="primary" variant="tonal" label>{{ rows.length }} {{ t('products') }}</v-chip>
			<v-spacer />
			<div class="future-referencing-zone__legend">
				<v-chip size="x-small" variant="outlined" label class="chip--current">H1</v-chip>
				<span>{{ t('CurrentStrate') }}</span>
				<v-chip size="x-small" variant="outlined" label class="chip--future">H2</v-chip>
				<span>{{ t('FutureStrate') }}</span>
			</div>
		</div>

		<v-alert v-if="!isCardView" type="info" variant="tonal" density="compact">{{ t('FutureReferencingInfo') }}</v-alert>

		<div v-if="isCardView && summary" class="future-referencing-zone__summary">
			<div class="future-referencing-zone__summary-total">{{ summary.total }} {{ t('FutureProductsConcerned') }}</div>
			<div class="future-referencing-zone__summary-breakdown">
				<span class="is-in">{{ summary.in }} {{ t('FutureEntries') }}</span>
				<span class="is-change">{{ summary.change }} {{ t('FutureChanges') }}</span>
				<span class="is-out">{{ summary.out }} {{ t('FutureExits') }}</span>
			</div>
		</div>

		<div v-if="!isCardView" class="future-referencing-zone__grid">
			<AgGrid :rowData="displayRows" :getRowId="getRowId" :colDefs="colDefs" :autoGroupColumnDef="autoGroupColumnDef" :groupDefaultExpanded="-1" rowGroupPanelShow="never" :sideBar="false" />
		</div>

		<TouchCardData v-else :row-data="displayRows" :columns="touchColumns" title-field="name" :subtitle-fields="['ean']" title-tags-field="productTags" row-key="id" metric-layout="rows" :group-columns="['rowGroupCategory']" :group-fields="{ rowGroupCategory: 'category' }" :group-count-label="groupCountLabel" sort-storage-key="futureReferencing" />
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import AgGrid from '@/components/agGrid/AgGrid.vue';
import TouchCardData from '@/components/agGrid/TouchCardData.vue';
import ChipRenderer from '@/components/agGrid/ChipRenderer.vue';
import { useTranslator } from '@/composables/useTranslator';
import { deviceUtils } from '@/utils/core/deviceUtils';

const t = useTranslator();
const zoneRef = ref(null);
const rows = ref([]);
const summary = ref(null);
const currency = ref('');
const locale = ref(undefined);
const revision = ref(0);

let _mountTarget = null;

// Same ref + resize-listener pattern as KpiBuilder: deviceUtils is not reactive.
// isLaptop (<1024px) is the widest of the three thresholds in use, which is the right bias here since the
// zone renders inside the iframe under the legacy visit chrome, so its usable width is well below innerWidth.
const isCardView = ref(deviceUtils.isLaptop());
const onResize = () => {
	isCardView.value = deviceUtils.isLaptop();
};

// #region CONTEXT

function handleContext(event) {
	const detail = event?.detail;
	if (!detail) return;
	// Legacy re-dispatches on every tab activation; ignore a payload we have already rendered.
	if (detail.revision && detail.revision === revision.value) return;

	revision.value = detail.revision || 0;
	rows.value = Array.isArray(detail.rows) ? detail.rows : [];
	summary.value = detail.summary || null;
	currency.value = detail.currency || '';
	locale.value = detail.locale || undefined;
}

onMounted(() => {
	// The event is dispatched on #vue-future-referencing-zone (the mount target = our parentElement).
	// DOM events bubble UP so we must listen on the parent, not on zoneRef (a child).
	_mountTarget = zoneRef.value?.parentElement;
	_mountTarget?.addEventListener('vue:future-referencing-context', handleContext);
	window.addEventListener('resize', onResize);

	// Handle the case where the event fired before Vue mounted (timing race).
	if (_mountTarget?._pendingFutureReferencingContext) {
		handleContext({ detail: _mountTarget._pendingFutureReferencingContext });
	}
});

onBeforeUnmount(() => {
	_mountTarget?.removeEventListener('vue:future-referencing-context', handleContext);
	window.removeEventListener('resize', onResize);
	_mountTarget = null;
});

// #endregion

// #region PRESENTATION

function formatDate(value) {
	if (!value) return '';
	const date = new Date(value);
	return isNaN(date.getTime()) ? '' : date.toLocaleDateString(locale.value);
}

function formatPrice(value) {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '';
	return `${numeric.toLocaleString(locale.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency.value}`.trim();
}

const MOVEMENT_CHIP_CLASS = { in: 'chip--in', change: 'chip--future', out: 'chip--out' };
const MOVEMENT_ICON_CLASS = { in: 'is-in', change: 'is-future', out: 'is-out' };

// Presentation-only derivation. The movement itself is decided by legacy (RG-01/RG-03) and never re-derived here.
const displayRows = computed(() =>
	rows.value.map((row) => ({
		...row,
		agreementDateLabel: formatDate(row.agreementDate),
		priceLabel: formatPrice(row.price),
		productTags: row.movement === 'in' ? [{ text: t('FutureEntry'), class: 'chip--in' }] : row.movement === 'out' ? [{ text: t('FutureExit'), class: 'chip--out' }] : [],
		typologyTags: row.typology ? [{ text: row.typology, class: 'chip--current' }] : [],
		futureTags: row.movement === 'out' ? [{ text: t('FutureExitSegment'), icon: 'subdirectory_arrow_right', class: 'chip--out' }] : row.futureStrate ? [{ text: row.futureStrate, class: MOVEMENT_CHIP_CLASS[row.movement] || 'chip--future' }] : [],
		dateTags: row.agreementDate ? [{ icon: 'calendar_month', class: MOVEMENT_ICON_CLASS[row.movement] || 'is-future' }] : [],
	}))
);

// #endregion

// #region DESKTOP GRID

const getRowId = (params) => String(params.data.id);

// Never string-compare ISO dates across the legacy boundary (see tasks/lessons.md).
function dateComparator(first, second) {
	return (first ? new Date(first).getTime() : 0) - (second ? new Date(second).getTime() : 0);
}

// aggFunc: null on every colDef - normalizeColDefs otherwise injects sum/count + enableValue and pollutes
// the values and row-group panels with meaningless measures.
const colDefs = computed(() => [
	{ colId: 'rowGroupCategory', field: 'category', headerName: t('Categories'), rowGroup: true, hide: true, enableRowGroup: false, suppressColumnsToolPanel: true, aggFunc: null },
	{ field: 'name', headerName: t('Label'), minWidth: 280, flex: 2, aggFunc: null, cellRenderer: ChipRenderer, cellRendererParams: { subField: 'ean', tagsField: 'productTags' } },
	{ field: 'typology', headerName: t('Typology'), minWidth: 130, aggFunc: null, cellRenderer: ChipRenderer, cellRendererParams: { textField: null, tagsField: 'typologyTags' } },
	{ field: 'futureStrate', headerName: t('FutureStrate'), minWidth: 150, aggFunc: null, cellRenderer: ChipRenderer, cellRendererParams: { textField: null, tagsField: 'futureTags' } },
	{ field: 'agreementDate', headerName: t('AgreementDate'), minWidth: 160, aggFunc: null, comparator: dateComparator, cellRenderer: ChipRenderer, cellRendererParams: { textField: 'agreementDateLabel', tagsField: 'dateTags' } },
	{ field: 'price', headerName: t('ProductPrice'), minWidth: 120, cellDataType: 'number', aggFunc: null, valueFormatter: ({ value }) => formatPrice(value) },
]);

const autoGroupColumnDef = computed(() => ({ headerName: t('Categories'), minWidth: 320 }));

// #endregion

// #region MOBILE CARDS

const touchColumns = computed(() => [
	{ field: 'typology', label: t('Typology'), type: 'chip', tagsField: 'typologyTags', sortType: 'text' },
	{ field: 'futureStrate', label: t('FutureStrate'), type: 'chip', tagsField: 'futureTags', sortType: 'text' },
	{ field: 'agreementDateLabel', label: t('AgreementDate'), sortType: 'text', formatMetric: (value) => value || '—' },
	{ field: 'price', label: t('Price'), formatMetric: formatPrice },
]);

// The default builder hides the count for single-product categories; here it is always meaningful.
const groupCountLabel = ({ rows: groupRows }) => `${groupRows.length} ${t('products')}`;

// #endregion
</script>
